// engine/fuzzyCategoryMatcher.js
import {
  BRAND_ALIASES,
  MODEL_HINTS,
  DOG_BREEDS,
  CAT_BREEDS,
  REAL_ESTATE_HINTS,
  GARDEN_STRUCTURES,
  INTENT_VERBS,
} from "./knowledgePacks";

// === Категорії (мапінг 1:1 з categoryVisualMap) ===
const CATS = {
  REAL_ESTATE: "Житло та нерухомість",
  TRANSPORT: "Транспорт",
  ELECTRONICS: "Гаджети",
  CAREER: "Карʼєра",
  SERVICES: "Майстри та послуги",
  HOME: "Дім та затишок",
  GARDEN: "Для саду та подвір'я",
  KIDS: "Малеча",
  PETS: "Домашні улюбленці",
  FASHION: "Стиль",
  SPORTS: "Активність та спорт",
  BEAUTY: "Б’юті",
  EDUCATION: "Знання та Навчання",
  RENTAL: "Оренда та прокат",
  PARTS: "Запчастини та аксесуари",
  ACCESSORIES: "Аксесуари",
  FOOD: "Продукти та їжа",
  HOBBY: "Хобі",
};

// === Нормалізація/стемінг/трансліт ===
const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .replaceAll("’", "'")
    .replaceAll("`", "'")
    .replace(/\s+/g, " ")
    .trim();

const stemUa = (t) =>
  t
    .replace(/(ами|ями|еві|ові|ах|ях|у|ю|а|я|и|і|ої|ою|ий|ій|им|єм|ем|ми|ти|ть|ш)$/g, "")
    .replace(/(ська|ський|ські|ському|ської)$/g, "");

const translitLite = (s) =>
  s
    .replace(/w/g, "в")
    .replace(/sch/g, "ш")
    .replace(/sh/g, "ш")
    .replace(/ch/g, "ч")
    .replace(/ya/g, "я")
    .replace(/yu/g, "ю")
    .replace(/yo/g, "йо")
    .replace(/zh/g, "ж");

// === Левенштейн (короткі слова) ===
const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 1; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
};

// === Токенізація (включно з біграмами) ===
const tokenize = (text) => {
  const raw = normalize(text);
  const tokens = raw.split(/[^a-zа-яёіїє0-9\-]+/i).filter(Boolean);
  const grams = new Set(tokens);
  for (let i = 0; i < tokens.length - 1; i++) {
    grams.add(tokens[i] + " " + tokens[i + 1]);
  }
  return Array.from(grams);
};

// === Перевірки об'єктів/брендів/моделей ===
const isBrand = (token) => {
  const t = normalize(token);
  for (const brand in BRAND_ALIASES) {
    for (const alias of BRAND_ALIASES[brand]) {
      const a = normalize(alias);
      if (t === a) return true;
      if (t.startsWith(a) && t.length - a.length <= 2) return true;
      if (Math.min(a.length, t.length) >= 4 && levenshtein(t, a) <= 1) return true;
    }
  }
  const tl = translitLite(t);
  if (tl !== t) return isBrand(tl);
  return false;
};

const hitsModel = (text) => {
  const raw = normalize(text);
  return MODEL_HINTS.some((rx) => rx.test(raw));
};

const isDogBreed = (token) => {
  const t = normalize(token);
  return DOG_BREEDS.some((b) => {
    const bb = normalize(b);
    if (t === bb) return true;
    if (t.startsWith(stemUa(bb)) && Math.abs(t.length - bb.length) <= 2) return true;
    return Math.min(bb.length, t.length) >= 5 && levenshtein(t, bb) <= 1;
  });
};

const isCatBreed = (token) => {
  const t = normalize(token);
  return CAT_BREEDS.some((b) => {
    const bb = normalize(b);
    if (t === bb) return true;
    if (t.startsWith(stemUa(bb)) && Math.abs(t.length - bb.length) <= 2) return true;
    return Math.min(bb.length, t.length) >= 5 && levenshtein(t, bb) <= 1;
  });
};

const isRealEstateObject = (token) => {
  const t = normalize(token);
  return REAL_ESTATE_HINTS.some((w) => {
    const ww = normalize(w);
    if (t === ww) return true;
    if (t.startsWith(stemUa(ww)) && Math.abs(t.length - ww.length) <= 2) return true;
    return Math.min(ww.length, t.length) >= 6 && levenshtein(t, ww) <= 1;
  });
};

const isGardenObject = (token) => {
  const t = normalize(token);
  return GARDEN_STRUCTURES.some((w) => {
    const ww = normalize(w);
    if (t === ww) return true;
    if (t.startsWith(stemUa(ww)) && Math.abs(t.length - ww.length) <= 2) return true;
    return Math.min(ww.length, t.length) >= 6 && levenshtein(t, ww) <= 1;
  });
};

// === Інтенції ===
const hasRentalIntent = (text) => {
  const raw = normalize(text);
  return INTENT_VERBS.rental.some((v) => raw.includes(normalize(v)));
};
const hasTradeIntent = (text) => {
  const raw = normalize(text);
  return INTENT_VERBS.trade.some((v) => raw.includes(normalize(v)));
};

// === Матчер ===
export class FuzzyCategoryMatcher {
  constructor(keywordCategoryMap, opts = {}) {
    this.map = keywordCategoryMap || {};
    this.opts = {
      maxEditDistance: 2,
      minScore: 0.52,
      enableTranslit: true,
      ...opts,
    };
    this.index = this._buildIndex(this.map);
  }

  _buildIndex(map) {
    const inv = {};
    for (const key in map) {
      const cat = map[key];
      const k = normalize(key);
      inv[k] = cat;
    }
    return inv;
  }

  _lookupFuzzy(token) {
    const t = normalize(token);
    let best = null;

    for (const w in this.index) {
      if (w === t) {
        return { category: this.index[w], score: 1, hit: w };
      }
      if (t.startsWith(w) && w.length >= 3) {
        const sc = Math.min(0.95, 0.6 + w.length / (t.length + 3));
        if (!best || sc > best.score) best = { category: this.index[w], score: sc, hit: w };
        continue;
      }
      if (w.startsWith(t) && t.length >= 3) {
        const sc = 0.55;
        if (!best || sc > best.score) best = { category: this.index[w], score: sc, hit: w };
        continue;
      }
      if (Math.min(w.length, t.length) >= 4) {
        const d = levenshtein(w, t);
        if (d <= this.opts.maxEditDistance) {
          const sc = 0.6 - 0.1 * (d - 1);
          if (!best || sc > best.score) best = { category: this.index[w], score: sc, hit: w };
        }
      }
    }
    return best;
  }

  detectCategories(text, topK = 1) {
    const tokens = tokenize(text);
    const scores = new Map();
    const hits = [];

    const add = (cat, score, why) => {
      if (!cat) return;
      scores.set(cat, (scores.get(cat) || 0) + score);
      hits.push({ cat, score, why });
    };

    // об'єктні сигнали
    for (const tok of tokens) {
      if (isBrand(tok)) add(CATS.TRANSPORT, 0.9, `brand:${tok}`);
      if (isDogBreed(tok) || isCatBreed(tok)) add(CATS.PETS, 0.9, `breed:${tok}`);
      if (isRealEstateObject(tok)) add(CATS.REAL_ESTATE, 0.9, `realestate:${tok}`);
      if (isGardenObject(tok)) add(CATS.GARDEN, 0.7, `garden:${tok}`);
    }
    if (hitsModel(text)) add(CATS.TRANSPORT, 0.9, "model:regex");

    // інтенції
    if (hasRentalIntent(text)) add(CATS.RENTAL, 0.6, "intent:rental");
    if (hasTradeIntent(text)) add(CATS.PARTS, 0.05, "intent:trade");

    // fallback fuzzy
    if (scores.size === 0) {
      let best = null;
      for (const tok of tokens) {
        const fz = this._lookupFuzzy(tok);
        if (fz && (!best || fz.score > best.score)) best = fz;
      }
      if (best) add(best.category, Math.max(this.opts.minScore, best.score), `fuzzy:${best.hit}`);
    }

    // нормалізація/сортування
    const arr = Array.from(scores.entries())
      .map(([category, score]) => ({ category, score: Math.min(1, score) }))
      .sort((a, b) => b.score - a.score);

    const filtered = arr.filter((x) => x.score >= this.opts.minScore);

    const top = (filtered.length ? filtered : arr).slice(0, topK);
    return top.map((t) => ({
      ...t,
      hits: hits.filter((h) => h.cat === t.category),
    }));
  }
}
